import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/Toast';
import {
  autoClaimQueueLead,
  claimQueueLead,
  fetchQueueLeads,
  fetchQueues,
} from '../api/queueService';
import { normalizeLead } from '../utils/leadModel';

const SORT_OPTIONS = [
  { value: 'sla_due_at', label: 'SLA Urgency' },
  { value: 'lead_score', label: 'Lead Score' },
  { value: 'created_at', label: 'Created At' },
];

const OWNER_BADGE_STYLES = {
  queue: { background: '#fef3c7', color: '#b45309', border: '#fcd34d' },
  agent: { background: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  unassigned: { background: '#e2e8f0', color: '#475569', border: '#cbd5e1' },
};

const SLA_BADGE_STYLES = {
  breached: { background: '#fee2e2', color: '#b91c1c', border: '#fecaca', label: 'SLA Breached' },
  'due-soon': { background: '#fef3c7', color: '#b45309', border: '#fde68a', label: 'Due Soon' },
  'on-track': { background: '#dcfce7', color: '#166534', border: '#86efac', label: 'On Track' },
};

function pillStyle({ background, color, border }) {
  return {
    background,
    color,
    border: `1px solid ${border}`,
    padding: '2px 10px',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function QueueWorklistPage() {
  const { role } = useAuth();
  const toast = useToast();
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState('');
  const [sort, setSort] = useState('sla_due_at');
  const [leads, setLeads] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [claimingLeadId, setClaimingLeadId] = useState(null);
  const [autoClaiming, setAutoClaiming] = useState(false);

  const isAdmin = role === 'admin';

  const refreshQueues = useCallback(async () => {
    const response = await fetchQueues();
    const nextQueues = response.queues || response.data?.queues || [];
    setQueues(nextQueues);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadQueues() {
      setLoadingQueues(true);
      try {
        const response = await fetchQueues();
        if (!mounted) {
          return;
        }
        const nextQueues = response.queues || response.data?.queues || [];
        setQueues(nextQueues);
        if (!selectedQueueId && nextQueues.length) {
          setSelectedQueueId(nextQueues[0].id);
        }
      } catch (err) {
        if (mounted) {
          toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to load queues.');
        }
      } finally {
        if (mounted) {
          setLoadingQueues(false);
        }
      }
    }

    loadQueues();
    return () => {
      mounted = false;
    };
  }, [selectedQueueId, toast]);

  const loadQueueLeads = useCallback(async () => {
    if (!selectedQueueId) {
      setLeads([]);
      return;
    }

    setLoadingLeads(true);
    try {
      const response = await fetchQueueLeads(selectedQueueId, { sort });
      setLeads((response.leads || response.data?.leads || []).map(normalizeLead));
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to load queue leads.');
    } finally {
      setLoadingLeads(false);
    }
  }, [selectedQueueId, sort, toast]);

  useEffect(() => {
    loadQueueLeads();
  }, [loadQueueLeads]);

  const selectedQueue = useMemo(
    () => queues.find((queue) => queue.id === selectedQueueId) || null,
    [queues, selectedQueueId]
  );

  const handleClaim = async (leadId) => {
    setClaimingLeadId(leadId);
    try {
      const response = await claimQueueLead(selectedQueueId, leadId);
      toast.success(response.message || 'Lead claimed successfully.');
      await loadQueueLeads();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to claim lead.');
    } finally {
      setClaimingLeadId(null);
    }
  };

  const handleAutoClaim = async () => {
    if (!selectedQueueId || !isAdmin) {
      return;
    }

    setAutoClaiming(true);
    try {
      const response = await autoClaimQueueLead(selectedQueueId);
      const claimedLead = response?.data?.lead || response?.lead || null;
      const claimedLeadName = claimedLead ? normalizeLead(claimedLead).fullName : null;

      toast.success(
        claimedLeadName
          ? `${response.message || 'Lead auto-claimed successfully.'} (${claimedLeadName})`
          : (response.message || 'Lead auto-claimed successfully.')
      );

      await Promise.all([loadQueueLeads(), refreshQueues()]);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to auto-claim lead.');
    } finally {
      setAutoClaiming(false);
    }
  };

  const renderSla = (lead) => {
    if (!lead.slaDueAt || lead.slaState === 'none') {
      return '—';
    }
    const palette = SLA_BADGE_STYLES[lead.slaState] || SLA_BADGE_STYLES['on-track'];
    return <span style={pillStyle(palette)} title={lead.slaDueAtLabel}>{palette.label}</span>;
  };

  return (
    <div>
      <div className="page-title-row">
        <div>
          <h2>Queue Worklist</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b' }}>
            Review queue-owned leads by SLA urgency and claim work directly from an active queue.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isAdmin ? (
            <button
              className="primary-btn"
              type="button"
              disabled={!selectedQueueId || autoClaiming}
              onClick={handleAutoClaim}
            >
              {autoClaiming ? 'Auto-claiming…' : 'Auto-Claim Next'}
            </button>
          ) : null}
          <button className="ghost-btn" type="button" onClick={loadQueueLeads}>
            <FiRefreshCw style={{ marginRight: 6 }} /> Refresh
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <select className="filter-select" value={selectedQueueId} onChange={(event) => setSelectedQueueId(event.target.value)}>
          <option value="">Select queue</option>
          {queues.map((queue) => (
            <option key={queue.id} value={queue.id}>{queue.name}</option>
          ))}
        </select>
        <select className="filter-select" value={sort} onChange={(event) => setSort(event.target.value)}>
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {loadingQueues ? <p>Loading queues…</p> : null}
      {selectedQueue ? (
        <div style={{ marginBottom: 16, color: '#475569' }}>
          <strong style={{ color: '#0f172a' }}>{selectedQueue.name}</strong>
          {' '}· {selectedQueue.current_size ?? 0} queued leads · SLA {selectedQueue.sla_hours ?? 24} hrs
        </div>
      ) : null}
      {loadingLeads ? <p>Loading queue leads…</p> : null}

      {!loadingLeads ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Owner</th>
              <th>SLA</th>
              <th>Lead Score</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.length ? (
              leads.map((lead) => (
                <tr key={lead.id}>
                  <td>{lead.fullName}</td>
                  <td>{lead.companyName}</td>
                  <td>
                    <span style={pillStyle(OWNER_BADGE_STYLES[lead.ownerType] || OWNER_BADGE_STYLES.unassigned)}>
                      {lead.ownerType === 'queue' ? selectedQueue?.name || 'Queue' : lead.assignedTo || 'Unassigned'}
                    </span>
                  </td>
                  <td>{renderSla(lead)}</td>
                  <td>{lead.leadScore ?? '—'}</td>
                  <td>{lead.status}</td>
                  <td>
                    <button
                      className="primary-btn"
                      type="button"
                      disabled={!selectedQueueId || claimingLeadId === lead.id}
                      onClick={() => handleClaim(lead.id)}
                    >
                      {claimingLeadId === lead.id ? 'Claiming…' : 'Claim Lead'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7">No queued leads found.</td></tr>
            )}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export default QueueWorklistPage;
